import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventCard from "@/components/EventCard";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { User, UserPlus, UserCheck, MessageSquare } from "lucide-react";

export default function PublicProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: p, isLoading } = useQuery({ queryKey: ["publicUser", userId], queryFn: async () => (await api.get(`/users/${userId}/public`)).data });

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + `/u/${userId}`)}`;
  };

  const toggleFollow = async () => {
    if (!user) { handleLogin(); return; }
    try {
      if (p.is_following) await api.delete(`/follow/${userId}`);
      else await api.post(`/follow/${userId}`);
      qc.invalidateQueries({ queryKey: ["publicUser", userId] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    } catch { toast.error("Error"); }
  };

  if (isLoading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!p) return <div className="py-24 text-center text-stone-400">404</div>;

  return (
    <>
      <Seo title={p.name} description={p.bio || `${p.name} — swiss-pagan.ch`} image={p.picture} canonicalPath={`/u/${userId}`} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-10 pb-8 border-b border-amber-900/30" data-testid="public-profile-header">
          {p.picture ? <img src={p.picture} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-amber-700/50" /> : <span className="w-24 h-24 rounded-full bg-amber-900/40 flex items-center justify-center"><User className="w-10 h-10 text-amber-400" /></span>}
          <div className="flex-1">
            <h1 className="font-serif text-3xl sm:text-4xl text-amber-100">{p.name}</h1>
            {p.bio && <p className="text-stone-400 mt-2 max-w-xl">{p.bio}</p>}
            <div className="font-mono text-xs text-stone-500 mt-2">{p.follower_count} {t("followers")}</div>
          </div>
          {!p.is_self && (
            <div className="flex gap-3">
              <button data-testid="follow-btn" onClick={toggleFollow} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold transition-all border ${p.is_following ? "bg-[#181A20] text-amber-400 border-amber-600/40" : "bg-gradient-to-r from-amber-600 to-amber-700 text-stone-950 border-amber-400/30"}`}>
                {p.is_following ? <><UserCheck className="w-4 h-4" /> {t("following")}</> : <><UserPlus className="w-4 h-4" /> {t("follow")}</>}
              </button>
              <button data-testid="profile-message-btn" onClick={() => (user ? navigate(`/messages/${userId}`) : handleLogin())} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium bg-[#181A20] text-stone-200 border border-[#272A34] hover:border-amber-500/40 transition-all">
                <MessageSquare className="w-4 h-4" /> {t("msg_button")}
              </button>
            </div>
          )}
        </div>

        <h2 className="font-serif text-2xl text-stone-100 mb-6">{t("organizer_events")}</h2>
        {p.events?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{p.events.map((e, i) => <EventCard key={e.slug} event={e} index={i} />)}</div>
        ) : (
          <p className="text-stone-400" data-testid="no-public-events">{t("no_public_events")}</p>
        )}
      </div>
    </>
  );
}
